import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useI18n, LANGS } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import CurrencySwitcher from "./CurrencySwitcher";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/shop", label: "Shop", id: "shop" },
  { to: "/shop?cat=Perfumes", label: "Perfumes", id: "perfumes" },
  { to: "/lookbook", label: "Lookbook", id: "lookbook" },
  { to: "/about", label: "Founder", id: "about" },
  { to: "/account", label: "Orders", id: "orders" },
];
const KEY = { home: "home", shop: "shop", perfumes: "perfumes", lookbook: "lookbook", about: "founder", orders: "orders", track: "track" };

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { count: wishCount } = useWishlist();
  const { t, lang, setLang } = useI18n();
  const { pathname } = useLocation();
  const dark = pathname === "/";
  const base = dark ? "text-ivory/75 hover:text-ivory" : "text-ink/75 hover:text-gold-dark";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-[60] transition-[background-color,border-color,backdrop-filter,box-shadow,color] duration-500 ${
        scrolled
          ? dark
            ? "bg-obsidian/75 backdrop-blur-md border-b border-gold/20 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]"
            : "bg-ivory/85 backdrop-blur-md border-b border-ink/10 shadow-[0_10px_40px_-20px_rgba(14,13,11,0.25)]"
          : "bg-transparent border-b border-transparent"
      } ${dark ? "text-ivory" : "text-ink"}`}
    >
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-brand-link"
          className="flex items-center gap-3 font-serif text-base md:text-lg tracking-[0.28em] uppercase whitespace-nowrap"
        >
          <motion.img
            src="/brand/logo-seal.png"
            alt="ATW seal"
            className="w-9 h-9 md:w-11 md:h-11"
            whileHover={{ rotate: 180 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          />
          <span className="hidden sm:flex items-center gap-3 normal-case">
            <span className="w-px h-9 bg-gradient-to-b from-transparent via-gold/70 to-transparent" />
            <span className="flex flex-col leading-none">
              <span
                className="font-serif italic text-2xl md:text-[26px] tracking-tight bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(115deg,#F0D67E 0%,#B9962B 45%,#8A6E1E 60%,#E9C767 100%)" }}
              >
                Dewan
              </span>
              <span className="mt-1.5 text-[8px] tracking-[0.55em] font-sans text-gold-dark/80">AROUND THE WORLD</span>
            </span>
          </span>
        </Link>

        <nav className={`hidden md:flex items-center gap-10 text-[11px] tracking-[0.25em] uppercase ${dark ? "text-ivory/65" : "text-ink/65"}`}>
          {[...LINKS, ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", id: "admin" }] : [])].map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={`group relative py-1 transition-colors duration-300 ${dark ? "hover:text-gold-light" : "hover:text-ink"}`}
            >
              {t(KEY[l.id]) || l.label}
              <span
                className={`absolute left-0 -bottom-0.5 h-px bg-gold transition-transform duration-500 origin-left ${
                  pathname === l.to ? "w-full scale-x-100" : "w-full scale-x-0 group-hover:scale-x-100"
                }`}
              />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <select
            data-testid="lang-switcher"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            aria-label="Language"
            className={`hidden sm:block bg-transparent text-[10px] tracking-[0.2em] uppercase cursor-pointer focus:outline-none ${dark ? "text-ivory/70" : "text-ink/70"}`}
          >
            {LANGS.map((l) => <option key={l.code} value={l.code} className="text-ink">{l.label}</option>)}
          </select>
          <CurrencySwitcher dark={dark} />
          <Link
            to={user ? "/account" : "/auth"}
            data-testid="nav-account-link"
            className={`p-2 transition-colors duration-300 ${base}`}
            aria-label="Account"
          >
            <User size={19} strokeWidth={1.5} />
          </Link>
          <Link to="/wishlist" data-testid="nav-wishlist-link" aria-label="Wishlist" className={`relative p-2 transition-colors duration-300 ${base}`}>
            <Heart size={19} strokeWidth={1.5} />
            {wishCount > 0 && <span data-testid="nav-wishlist-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-gold text-ink text-[9px] flex items-center justify-center">{wishCount}</span>}
          </Link>
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className={`relative p-2 transition-colors duration-300 ${base}`}
            aria-label="Open cart"
          >
            <ShoppingBag size={19} strokeWidth={1.5} />
            <AnimatePresence>
              {count > 0 && (
                <motion.span
                  key={count}
                  data-testid="nav-cart-count"
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.4, opacity: 0 }}
                  className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 bg-gold text-ivory text-[10px] font-semibold flex items-center justify-center"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button
            data-testid="nav-menu-button"
            className={`md:hidden p-2 ${dark ? "text-ivory/75" : "text-ink/75"}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className={`md:hidden overflow-hidden backdrop-blur-md border-t ${dark ? "bg-obsidian/95 border-gold/20" : "bg-ivory/95 border-ink/10"}`}
          >
            <div className={`px-6 py-6 flex flex-col gap-5 text-xs tracking-[0.25em] uppercase ${dark ? "text-ivory/80" : "text-ink/70"}`}>
              {LINKS.map((l) => (
                <Link key={l.id} to={l.to} data-testid={`nav-mobile-${l.id}-link`} onClick={() => setMenuOpen(false)}>
                  {t(KEY[l.id]) || l.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
